import React, { useState, useMemo } from "react"

// import components
import { Row, Col, Label, FormGroup, Card, CardBody, Button } from "reactstrap"
import Datetime from "react-datetime"
import { CSVLink } from "react-csv"
import ReactTable from "react-table"
import Select from "react-select"
import { getOptions, useQueryData } from "utils"
import { useQuery } from "@apollo/react-hooks"
import { GET_CHATS, GET_COMPANIES, GET_USERS } from "../gql"

// main component
const OrderExportContainer = () => {
  const { data } = useQueryData(GET_CHATS, {
    variables: {},
    fetchPolicy: "cache-and-network"
  })

  const { data: companyData } = useQuery(GET_COMPANIES, {
    fetchPolicy: "cache-and-network"
  })

  const { data: userData } = useQuery(GET_USERS, {
    fetchPolicy: "cache-and-network"
  })

  const [selectedCompany, setSelectedCompany] = useState()
  const [selectedUser, setSelectedUser] = useState()
  const [startDate, setStartDate] = useState()
  const [endDate, setEndDate] = useState()

  const fieldsDefinition = useMemo(() => {
    return [
      {
        Header: "id",
        accessor: "id",
        show: false
      },
      {
        id: "companyName",
        accessor: (data) => data.chatRoom.client.company.name,
        Header: "公司",
        style: columStyle
      },
      {
        id: "sender",
        Header: "發送者",
        style: columStyle,
        accessor: (data) => data.sender.name
      },
      {
        id: "receiver",
        Header: "接收者",
        style: columStyle,
        accessor: (data) => {
          return data.sender.company.name === "GETECH"
            ? data.chatRoom.client.name
            : "GETECH"
        }
      },
      {
        id: "type",
        accessor: (data) => data?.type,
        Header: "發送類型",
        style: columStyle
      },
      {
        id: "createdAt",
        accessor: "createdAt",
        Header: "發送時間",
        style: columStyle
      },
      {
        id: "content",
        accessor: "content",
        Header: "訊息內容",
        style: columStyle
      }
    ]
  }, [])

  const filteredResult = useMemo(() => {
    return (
      data?.chatMessagesByUserAndTimeInterval?.filter((item) => {
        let contain = false
        !selectedCompany
          ? (contain = true)
          : (selectedCompany.value === item.sender.company.id ||
              selectedCompany.value === item.chatRoom.client.company.id) &&
            (contain = true)

        if (selectedUser) {
          selectedUser.value === item.sender.id ||
          selectedUser.value === item.chatRoom.client.id
            ? (contain = true)
            : (contain = false)
        }

        if (
          (endDate && item.createdAt >= endDate) ||
          (startDate && item.createdAt <= startDate)
        ) {
          contain = false
        }
        return contain
      }) || []
    )
  }, [data, selectedCompany, selectedUser, startDate, endDate])

  const makeExportDataRow = (chat) => {
    const row = {}
    row.companyName = chat.chatRoom.client.company.name
    row.content = chat.content
    row.type = chat.type
    row.userName = chat.sender.name
    row.receiverName =
      chat.sender.company.name === "GETECH"
        ? chat.chatRoom.client.name
        : "GETECH"
    row.createdAt = chat.createdAt
    return row
  }

  const makeExportData = () => {
    const result = []
    filteredResult.forEach((chat) => {
      result.push(makeExportDataRow(chat))
    })
    return result
  }

  const exportHeader = [
    { label: "公司", key: "companyName" },
    { label: "發送者", key: "userName" },
    { label: "發送時間", key: "createdAt" },
    { label: "接收者", key: "receiverName" },
    { label: "發送類型", key: "type" },
    { label: "訊息內容", key: "content" }
  ]

  return (
    <>
      <Row className="mb-3">
        <Col md={12}>
          <FormGroup>
            <Label for="orderNumber">客戶</Label>
            <Select
              placeholder="選擇客戶"
              clearable={true}
              isMulti={false}
              value={selectedCompany}
              options={getOptions(companyData?.companies?.data, ["name"]).filter(
                (item) => item.label !== "GETECH"
              )}
              onChange={(option) => {
                setSelectedCompany(option)
                setSelectedUser(null)
              }}
            />
          </FormGroup>
        </Col>
        <Col md={12}>
          <FormGroup>
            <Label for="orderNumber">使用者</Label>
            <Select
              placeholder="選擇使用者"
              clearable={true}
              isMulti={false}
              value={selectedUser}
              options={
                selectedCompany &&
                getOptions(
                  userData?.users?.data.filter(
                    (user) => user.company.id === selectedCompany.value
                  ),
                  ["name"]
                )
              }
              onChange={(option) => {
                setSelectedUser(option)
              }}
            />
          </FormGroup>
        </Col>
        <Col md={12}>
          <FormGroup>
            <Label for="orderNumber">開始日期</Label>
            <Datetime
              dateFormat="YYYY-MM-DD"
              closeOnSelect={true}
              closeOnTab={true}
              value={startDate}
              onChange={(date) => {
                setStartDate(date.format("YYYY-MM-DD"))
              }}
              inputProps={{ placeholder: "選擇開始日期", readOnly: true }}
            />
          </FormGroup>
        </Col>
        <Col md={12}>
          <FormGroup>
            <Label for="orderNumber">結束日期</Label>
            <Datetime
              dateFormat="YYYY-MM-DD"
              closeOnSelect={true}
              closeOnTab={true}
              value={endDate}
              onChange={(date) => {
                setEndDate(date.format("YYYY-MM-DD"))
              }}
              inputProps={{ placeholder: "選擇結束日期", readOnly: true }}
            />
          </FormGroup>
        </Col>
        <Col md={12}>
          <FormGroup>
            <CSVLink
              headers={exportHeader}
              data={makeExportData()}
              filename={"客戶聊天資料.csv"}
            >
              <Button size="sm" round="true" icon="true" className="mr-2">
                <i className="fas fa-download" />
              </Button>
            </CSVLink>
          </FormGroup>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col md={12}>
          {filteredResult?.length > 0
            ? `共有 ${filteredResult.length} 則訊息可以匯出`
            : "沒有訊息可以匯出"}
        </Col>
      </Row>
      <Card>
        <CardBody>
          <ReactTable
            defaultPageSize={10}
            getTdProps={() => ({
              style: {
                display: "flex",
                flexDirection: "column",
                justifyContent: "center"
              }
            })}
            data={filteredResult}
            columns={[...fieldsDefinition]}
            showPagination={true}
          />
        </CardBody>
      </Card>
    </>
  )
}
const columStyle = { whiteSpace: "unset", textAlign: "center" }
export default OrderExportContainer
